import { JSX } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { useSidebarState } from 'react-admin';
import { useMenuSurface } from './menuSurface';

export interface MenuSectionHeaderProps {
    label: string;
    /** Referenced by the group's aria-labelledby so screen readers announce the grouping. */
    id?: string;
    /** The first heading in a section sits flush to the top, the rest are spaced above. */
    first?: boolean;
}

/**
 * The label that separates groups of links inside a section.
 *
 * It used to be body-sized text over a 2px rule, which took as much vertical
 * room as two of the links it was labelling and read as the more important of
 * the two. It is now an eyebrow with a hairline running off to the right, so a
 * section is a list with quiet dividers in it rather than a stack of headings.
 */
const wrapperStyles: (first: boolean) => SxProps<Theme> = (first: boolean): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    margin: first ? '2px 0 4px' : '12px 0 4px',
    padding: '0 12px 0 6px',
});

const labelStyles: SxProps<Theme> = (theme: Theme) => ({
    flex: '0 0 auto',
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    lineHeight: 1,
    color: theme.palette.text.secondary,
    opacity: 0.75,
});

const ruleStyles: SxProps<Theme> = (theme: Theme) => ({
    flex: 1,
    height: '1px',
    backgroundColor: theme.palette.divider,
});

export const MenuSectionHeader: (props: MenuSectionHeaderProps) => JSX.Element | null =
    ({ label, id, first = false }: MenuSectionHeaderProps): JSX.Element | null => {
        const [sidebarOpen] = useSidebarState();
        const surface = useMenuSurface();

        // There is no room for a text heading in a 60px rail. A flyout is not the
        // rail, though, and while one is open the sidebar is closed — so asking
        // `useSidebarState` alone would hide the headings exactly where they matter.
        if (!sidebarOpen && surface !== 'flyout') {
            return null;
        }

        return (
            <Box id={ id } sx={ wrapperStyles(first) }>
                <Box component="span" sx={ labelStyles }>{ label }</Box>
                <Box sx={ ruleStyles } />
            </Box>
        );
    };
